import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { AdminUserDetail } from "@/features/admin/admin.types";

type AdminUserCardProps = {
  detail: AdminUserDetail | null;
  error?: string | null;
  loading?: boolean;
  mode?: "users" | "credits" | "bans";
  onBan?: () => void;
  onChangeTier?: () => void;
  onDeductCredits?: () => void;
  onDeleteAccount?: () => void;
  onGrantCredits?: () => void;
  onOpenPqr?: (ticketRef: string) => void;
  onUnban?: () => void;
};

function formatDate(value: string | null, locale: string, unavailableLabel: string) {
  if (!value) {
    return unavailableLabel;
  }

  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function AdminUserCard({
  detail,
  error,
  loading = false,
  mode = "users",
  onBan,
  onChangeTier,
  onDeductCredits,
  onDeleteAccount,
  onGrantCredits,
  onOpenPqr,
  onUnban,
}: AdminUserCardProps) {
  const { i18n, t } = useTranslation();

  if (loading && !detail) {
    return (
      <div className="rounded-[1.6rem] border border-white/10 bg-black/20 px-5 py-10 text-sm text-[#d7cab9]/72">
        {t("adminWorkspace.detail.loading")}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-[1.6rem] border border-white/10 bg-[#5a2220]/25 px-5 py-10 text-sm text-[#f3d4cf]">
        {error}
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="rounded-[1.6rem] border border-dashed border-white/10 bg-black/20 px-5 py-10 text-sm text-[#d7cab9]/72">
        {t("adminWorkspace.detail.empty")}
      </div>
    );
  }

  const showCreditActions = mode === "users" || mode === "credits";
  const showBanActions = mode === "users" || mode === "bans";

  return (
    <div className="space-y-4 rounded-[1.6rem] border border-white/10 bg-black/20 p-5">
      <div className="flex flex-col gap-4 border-b border-white/8 pb-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-3xl text-[#fff7ef]">
              {[detail.firstName, detail.lastName].filter(Boolean).join(" ").trim() || detail.email}
            </h2>
            {detail.testAccount ? <Badge variant="outline">{t("adminWorkspace.detail.testAccount")}</Badge> : null}
            {detail.isAdmin ? <Badge variant="outline">{t("adminWorkspace.detail.admin")}</Badge> : null}
          </div>
          <p className="text-sm text-[#d8c8b3]/75">{detail.email}</p>
          <div className="flex flex-wrap gap-2 text-xs uppercase tracking-[0.18em] text-[#b69d7e]">
            <span>{t(`common.tiers.${detail.tier}`)}</span>
            <span>{t(`adminWorkspace.billingStatuses.${detail.billingStatus}`, { defaultValue: detail.billingStatus.replaceAll("_", " ") })}</span>
            <span>{t(`common.languageNames.${detail.languagePref}`)}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {showCreditActions ? (
            <>
              <Button
                disabled={!onChangeTier}
                size="sm"
                type="button"
                variant="outline"
                onClick={onChangeTier}
              >
                {t("adminWorkspace.detail.actions.changeTier")}
              </Button>
              <Button size="sm" type="button" variant="outline" onClick={onGrantCredits}>
                {t("adminWorkspace.detail.actions.grantCredits")}
              </Button>
              <Button size="sm" type="button" variant="outline" onClick={onDeductCredits}>
                {t("adminWorkspace.detail.actions.deductCredits")}
              </Button>
            </>
          ) : null}
          {showBanActions ? (
            detail.activeBanExpiresAt ? (
              <Button size="sm" type="button" variant="destructive" onClick={onUnban}>
                {t("adminWorkspace.detail.actions.unban")}
              </Button>
            ) : (
              <Button size="sm" type="button" variant="destructive" onClick={onBan}>
                {t("adminWorkspace.detail.actions.ban")}
              </Button>
            )
          ) : null}
          {mode === "users" ? (
            <Button
              disabled={Boolean(detail.deletedAt)}
              size="sm"
              type="button"
              variant="destructive"
              onClick={onDeleteAccount}
            >
              {t("adminWorkspace.detail.actions.deleteAccount")}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <StatCard label={t("adminWorkspace.detail.stats.registered")} value={formatDate(detail.registeredAt, i18n.language, t("adminWorkspace.detail.values.notAvailable"))} />
        <StatCard label={t("adminWorkspace.detail.stats.lastLogin")} value={formatDate(detail.lastLoginAt, i18n.language, t("adminWorkspace.detail.values.notAvailable"))} />
        <StatCard label={t("adminWorkspace.detail.stats.creditBalance")} value={`${detail.creditBalance}`} />
        <StatCard label={t("adminWorkspace.detail.stats.activeBan")} value={detail.activeBanExpiresAt ? formatDate(detail.activeBanExpiresAt, i18n.language, t("adminWorkspace.detail.values.notAvailable")) : t("adminWorkspace.detail.values.noActiveBan")} />
        <StatCard
          label={t("adminWorkspace.detail.stats.deletion")}
          value={
            detail.deletedAt
              ? t("adminWorkspace.detail.values.deleted", {
                date: formatDate(detail.deletedAt, i18n.language, t("adminWorkspace.detail.values.notAvailable")),
              })
              : detail.deletionScheduledAt
                ? t("adminWorkspace.detail.values.pendingDeletion", {
                  date: formatDate(detail.deletionScheduledAt, i18n.language, t("adminWorkspace.detail.values.notAvailable")),
                })
                : t("adminWorkspace.detail.values.noDeletion")
          }
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <HistorySection
          emptyLabel={t("adminWorkspace.detail.noEntries")}
          items={detail.creditTransactions.map((item) => (
            <div key={item.id} className="space-y-1 rounded-[1.2rem] border border-white/8 bg-white/[0.03] px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-[#fff7ef]">{item.feature}</p>
                <p className={`text-sm font-semibold ${item.amount >= 0 ? "text-[#d6f0cf]" : "text-[#ffd0c8]"}`}>
                  {item.amount >= 0 ? `+${item.amount}` : item.amount}
                </p>
              </div>
              <p className="text-xs text-[#d8c8b3]/65">{formatDate(item.createdAt, i18n.language, t("adminWorkspace.detail.values.notAvailable"))}</p>
            </div>
          ))}
          title={t("adminWorkspace.detail.sections.creditHistory")}
        />

        <HistorySection
          emptyLabel={t("adminWorkspace.detail.noEntries")}
          items={detail.banHistory.map((item) => (
            <div key={item.id} className="space-y-1 rounded-[1.2rem] border border-white/8 bg-white/[0.03] px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-[#fff7ef]">{item.reason}</p>
                <p className="text-xs text-[#d8c8b3]/65">{formatDate(item.bannedAt, i18n.language, t("adminWorkspace.detail.values.notAvailable"))}</p>
              </div>
              <p className="text-xs text-[#d8c8b3]/65">
                {item.liftedAt
                  ? t("adminWorkspace.detail.values.lifted", {
                      date: formatDate(item.liftedAt, i18n.language, t("adminWorkspace.detail.values.notAvailable")),
                    })
                  : item.expiresAt
                    ? t("adminWorkspace.detail.values.activeUntil", {
                        date: formatDate(item.expiresAt, i18n.language, t("adminWorkspace.detail.values.notAvailable")),
                      })
                    : t("adminWorkspace.detail.values.permanent")}
              </p>
            </div>
          ))}
          title={t("adminWorkspace.detail.sections.banHistory")}
        />

        <HistorySection
          emptyLabel={t("adminWorkspace.detail.noEntries")}
          items={detail.pqrSubmissions.map((item) => (
            <button
              className="w-full rounded-[1.2rem] border border-white/8 bg-white/[0.03] px-4 py-3 text-left transition hover:bg-white/[0.06]"
              key={item.id}
              type="button"
              onClick={() => onOpenPqr?.(item.ticketRef)}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-[#fff7ef]">{item.subject}</p>
                <p className="text-xs uppercase tracking-[0.18em] text-[#b69d7e]">{t(`pqr.status.${item.status}`)}</p>
              </div>
              <p className="mt-1 text-xs text-[#d8c8b3]/65">{item.ticketRef}</p>
            </button>
          ))}
          title={t("adminWorkspace.detail.sections.pqrSubmissions")}
        />
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[1.2rem] border border-white/8 bg-white/[0.03] px-4 py-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#b69d7e]">{label}</p>
      <p className="mt-2 text-sm leading-6 text-[#fff7ef]">{value}</p>
    </div>
  );
}

function HistorySection({
  emptyLabel,
  items,
  title,
}: {
  emptyLabel: string;
  items: ReactNode[];
  title: string;
}) {
  return (
    <section className="space-y-3 rounded-[1.4rem] border border-white/8 bg-white/[0.025] p-4">
      <h3 className="text-sm font-semibold uppercase tracking-[0.22em] text-[#b69d7e]">{title}</h3>
      {items.length ? items : (
        <div className="rounded-[1.2rem] border border-dashed border-white/8 px-4 py-6 text-sm text-[#d7cab9]/62">
          {emptyLabel}
        </div>
      )}
    </section>
  );
}
